export type PolicyOutcome = "allow" | "confirm" | "deny";
export type PolicySource = "manifest" | "permissionProfile" | "builtin";
export type PolicyPredicateSource = "input" | "object" | "stateMachine";
export type PolicyOperator =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "exists";

export type ConditionIR =
  | { kind: "always" }
  | { kind: "all"; conditions: ConditionIR[] }
  | { kind: "any"; conditions: ConditionIR[] }
  | { kind: "not"; condition: ConditionIR }
  | {
      kind: "predicate";
      source: PolicyPredicateSource;
      path: string[];
      operator: PolicyOperator;
      value?: string | number | boolean | null;
      machine?: string;
    };

export type PolicyOrigin =
  | { kind: "manifest"; manifestId?: string; effectKey: string }
  | {
      kind: "permissionProfile";
      profileName: string;
      permissionProfileVersionId?: string;
      source: PolicySource;
    };

export interface PolicyRuleIR {
  id: string;
  origin: PolicyOrigin;
  outcome: PolicyOutcome;
  reason?: string;
  condition: ConditionIR;
  summary: string;
}

export interface MatchedPolicy {
  id: string;
  outcome: PolicyOutcome;
  origin: PolicyOrigin;
  reason?: string;
  summary: string;
}

export interface PolicyDecision {
  outcome: PolicyOutcome;
  matches: MatchedPolicy[];
}

export interface PolicyEvaluationContext {
  input?: Record<string, unknown> | null;
  object?: Record<string, unknown> | null;
  stateMachines?: Record<string, string | null | undefined> | null;
}

export interface EffectDeclarationRef {
  effectKey: string;
  name: string;
  attachedClass?: string | null;
  isStatic?: boolean;
  inputSchema?: Record<string, unknown> | null;
  metamodels?: Record<string, unknown> | null;
}

export interface PermissionProfileFile {
  schemaVersion?: number;
  name: string;
  label?: string;
  description?: string;
  extends?: string;
  defaultsOwnerName?: string;
  source?: PolicySource;
  sourcePath?: string;
  defaults?: {
    actionPolicy?: PolicyOutcome;
  };
  policies?: PermissionPolicySpec[];
  actions?: PermissionActionSpec[];
}

export interface PermissionPolicySpec {
  id?: string;
  ownerProfileName?: string;
  action?: string;
  on?: string;
  decision?: PolicyOutcome;
  outcome?: PolicyOutcome;
  reason?: string;
  when?: unknown;
}

export interface PermissionActionSpec {
  id?: string;
  ownerProfileName?: string;
  action: string;
  on?: string;
  allow?: "always";
  deny?: "always";
  confirm?: "always";
  reason?: string;
  allowWhen?: ConditionalPolicySpec[];
  confirmWhen?: ConditionalPolicySpec[];
  denyWhen?: ConditionalPolicySpec[];
  limits?: LimitPolicySpec[];
}

export interface ConditionalPolicySpec {
  id?: string;
  reason?: string;
  when: unknown;
}

export interface LimitPolicySpec {
  id?: string;
  input: string | string[];
  upTo?: number;
  above?: number;
  decision: PolicyOutcome;
  reason?: string;
}

export interface CompiledPermissionProfile {
  name: string;
  label?: string;
  description?: string;
  source: PolicySource;
  sourcePath?: string;
  defaultActionPolicy: PolicyOutcome;
  defaultActionPolicyOwner?: string;
  digest: string;
  actionRules: Record<string, PolicyRuleIR[]>;
  actionVisibility: string[];
  summaries: string[];
  flattened: PermissionProfileFile;
}

export interface CompiledPolicyBundle {
  schemaVersion: 1;
  buildId?: string;
  sandboxId?: string;
  manifestDigest?: string;
  permissionProfilesDigest: string;
  bundleDigest: string;
  generatedAt: string;
  effects: Array<{
    effectKey: string;
    name: string;
    attachedClass?: string | null;
    isStatic: boolean;
  }>;
  manifestRules: Record<string, PolicyRuleIR[]>;
  profiles: Record<string, CompiledPermissionProfile>;
}

export interface CompilePolicyBundleInput {
  buildId?: string;
  sandboxId?: string;
  manifestId?: string;
  manifestDigest?: string;
  effects: EffectDeclarationRef[];
  profileSources?: PermissionProfileFile[];
}

export interface CompilePolicyBundleResult {
  bundle: CompiledPolicyBundle;
  errors: string[];
  warnings: string[];
}

const DECISIONS = new Set<PolicyOutcome>(["allow", "confirm", "deny"]);
const BUILTIN_PROFILE_NAMES = ["allow-all", "confirm-all", "deny-all"] as const;

export function getBuiltinPermissionProfiles(): PermissionProfileFile[] {
  return [
    {
      schemaVersion: 1,
      name: "allow-all",
      label: "Allow all",
      description:
        "Every declared action is visible unless a manifest policy denies it.",
      source: "builtin",
      defaults: { actionPolicy: "allow" },
      policies: [],
      actions: [],
    },
    {
      schemaVersion: 1,
      name: "confirm-all",
      label: "Confirm all",
      description:
        "Every declared action requires user confirmation before execution.",
      source: "builtin",
      defaults: { actionPolicy: "confirm" },
      policies: [],
      actions: [],
    },
    {
      schemaVersion: 1,
      name: "deny-all",
      label: "Deny all",
      description: "No declared action is invocable.",
      source: "builtin",
      defaults: { actionPolicy: "deny" },
      policies: [],
      actions: [],
    },
  ];
}

export function stableStringify(value: unknown): string {
  return JSON.stringify(sortJson(value));
}

export async function digestJson(value: unknown): Promise<string> {
  const payload = stableStringify(value);
  const cryptoLike = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoLike?.subtle) {
    const data = new TextEncoder().encode(payload);
    const hash = await cryptoLike.subtle.digest("SHA-256", data);
    const hex = Array.from(new Uint8Array(hash))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
    return `sha256:${hex}`;
  }

  let hash = 0x811c9dc5;
  for (let index = 0; index < payload.length; index += 1) {
    hash ^= payload.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function sortJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortJson);
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, child]) => [key, sortJson(child)]),
    );
  }
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeProfileName(name: unknown): string | null {
  if (typeof name !== "string") return null;
  const trimmed = name.trim();
  if (!/^[a-z0-9][a-z0-9._-]{1,63}$/.test(trimmed)) return null;
  return trimmed;
}

function normalizeDecision(value: unknown): PolicyOutcome | null {
  return DECISIONS.has(value as PolicyOutcome)
    ? (value as PolicyOutcome)
    : null;
}

function normalizePath(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((part) => String(part)).filter(Boolean);
  }
  if (typeof value === "string") {
    return value.includes(".") ? value.split(".").filter(Boolean) : [value];
  }
  return [];
}

function firstDefinedValue(spec: Record<string, unknown>) {
  if ("value" in spec) return spec.value as string | number | boolean | null;
  if ("stringValue" in spec) return spec.stringValue as string;
  if ("numberValue" in spec) return spec.numberValue as number;
  if ("booleanValue" in spec) return spec.booleanValue as boolean;
  if ("state" in spec) return spec.state as string;
  return undefined;
}

export function normalizeCondition(input: unknown): ConditionIR {
  if (input === undefined || input === null) return { kind: "always" };
  if (!isRecord(input)) {
    throw new Error("Policy condition must be an object");
  }

  if (Array.isArray(input.all)) {
    return {
      kind: "all",
      conditions: input.all.map((item) => normalizeCondition(item)),
    };
  }
  if (Array.isArray(input.any)) {
    return {
      kind: "any",
      conditions: input.any.map((item) => normalizeCondition(item)),
    };
  }
  if (input.not !== undefined) {
    return { kind: "not", condition: normalizeCondition(input.not) };
  }

  for (const source of ["input", "object", "stateMachine"] as const) {
    const raw = input[source];
    if (!isRecord(raw)) continue;
    const operator = raw.operator;
    if (
      operator !== "eq" &&
      operator !== "neq" &&
      operator !== "gt" &&
      operator !== "gte" &&
      operator !== "lt" &&
      operator !== "lte" &&
      operator !== "contains" &&
      operator !== "not_contains" &&
      operator !== "starts_with" &&
      operator !== "ends_with" &&
      operator !== "exists"
    ) {
      throw new Error(`Unsupported policy operator: ${String(operator)}`);
    }

    if (source === "stateMachine") {
      const machine = typeof raw.machine === "string" ? raw.machine : "";
      if (!machine) throw new Error("stateMachine condition requires machine");
      return {
        kind: "predicate",
        source,
        path: [machine],
        machine,
        operator,
        value: firstDefinedValue(raw),
      };
    }

    const path = normalizePath(raw.path ?? raw.field ?? raw.input);
    if (path.length === 0) {
      throw new Error(`${source} condition requires a path`);
    }
    return {
      kind: "predicate",
      source,
      path,
      operator,
      value: firstDefinedValue(raw),
    };
  }

  throw new Error(
    "Policy condition must contain all, any, not, input, object, or stateMachine",
  );
}

function readPath(
  root: unknown,
  path: string[],
): { exists: boolean; value: unknown } {
  let current = root;
  for (const part of path) {
    if (!isRecord(current) && !Array.isArray(current)) {
      return { exists: false, value: undefined };
    }
    if (!(part in (current as Record<string, unknown>))) {
      return { exists: false, value: undefined };
    }
    current = (current as Record<string, unknown>)[part];
  }
  return { exists: true, value: current };
}

export function evaluateCondition(
  condition: ConditionIR,
  context: PolicyEvaluationContext,
): boolean {
  switch (condition.kind) {
    case "always":
      return true;
    case "all":
      return condition.conditions.every((child) =>
        evaluateCondition(child, context),
      );
    case "any":
      return condition.conditions.some((child) =>
        evaluateCondition(child, context),
      );
    case "not":
      return !evaluateCondition(condition.condition, context);
    case "predicate":
      return evaluatePredicate(condition, context);
  }
}

function evaluatePredicate(
  predicate: Extract<ConditionIR, { kind: "predicate" }>,
  context: PolicyEvaluationContext,
): boolean {
  const target =
    predicate.source === "input"
      ? context.input
      : predicate.source === "object"
        ? context.object
        : context.stateMachines;
  const { exists, value } = readPath(target || {}, predicate.path);

  if (predicate.operator === "exists") return exists;
  if (!exists) return false;

  const expected = predicate.value;
  switch (predicate.operator) {
    case "eq":
      return value === expected;
    case "neq":
      return value !== expected;
    case "gt":
      return (
        typeof value === "number" &&
        typeof expected === "number" &&
        value > expected
      );
    case "gte":
      return (
        typeof value === "number" &&
        typeof expected === "number" &&
        value >= expected
      );
    case "lt":
      return (
        typeof value === "number" &&
        typeof expected === "number" &&
        value < expected
      );
    case "lte":
      return (
        typeof value === "number" &&
        typeof expected === "number" &&
        value <= expected
      );
    case "contains":
      return Array.isArray(value)
        ? value.includes(expected)
        : typeof value === "string" &&
            typeof expected === "string" &&
            value.includes(expected);
    case "not_contains":
      return Array.isArray(value)
        ? !value.includes(expected)
        : typeof value === "string" &&
            typeof expected === "string" &&
            !value.includes(expected);
    case "starts_with":
      return (
        typeof value === "string" &&
        typeof expected === "string" &&
        value.startsWith(expected)
      );
    case "ends_with":
      return (
        typeof value === "string" &&
        typeof expected === "string" &&
        value.endsWith(expected)
      );
  }
}

function matchRule(
  rule: PolicyRuleIR,
  context: PolicyEvaluationContext,
): MatchedPolicy | null {
  if (!evaluateCondition(rule.condition, context)) return null;
  return {
    id: rule.id,
    outcome: rule.outcome,
    origin: rule.origin,
    reason: rule.reason,
    summary: rule.summary,
  };
}

export function evaluatePolicies(input: {
  manifestRules?: PolicyRuleIR[];
  profileRules?: PolicyRuleIR[];
  defaultActionPolicy?: PolicyOutcome;
  context?: PolicyEvaluationContext;
}): PolicyDecision {
  const context = input.context || {};
  const manifestRules = input.manifestRules || [];
  const profileRules = input.profileRules || [];
  const defaultActionPolicy = input.defaultActionPolicy || "deny";

  const buckets: Array<{ outcome: PolicyOutcome; rules: PolicyRuleIR[] }> = [
    {
      outcome: "deny",
      rules: manifestRules.filter((rule) => rule.outcome === "deny"),
    },
    {
      outcome: "deny",
      rules: profileRules.filter((rule) => rule.outcome === "deny"),
    },
    {
      outcome: "confirm",
      rules: manifestRules.filter((rule) => rule.outcome === "confirm"),
    },
    {
      outcome: "confirm",
      rules: profileRules.filter((rule) => rule.outcome === "confirm"),
    },
    {
      outcome: "allow",
      rules: profileRules.filter((rule) => rule.outcome === "allow"),
    },
  ];

  for (const bucket of buckets) {
    const matches = bucket.rules
      .map((rule) => matchRule(rule, context))
      .filter((match): match is MatchedPolicy => match !== null);
    if (matches.length > 0) return { outcome: bucket.outcome, matches };
  }

  return {
    outcome: defaultActionPolicy,
    matches:
      defaultActionPolicy === "deny"
        ? []
        : [
            {
              id: `profile-default-${defaultActionPolicy}`,
              outcome: defaultActionPolicy,
              origin: {
                kind: "permissionProfile",
                profileName: "active",
                source: "permissionProfile",
              },
              summary: `Profile default policy is ${defaultActionPolicy}`,
            },
          ],
  };
}

function ruleSummary(
  outcome: PolicyOutcome,
  reason: string | undefined,
  condition: ConditionIR,
): string {
  if (reason) return reason;
  if (condition.kind === "always") return `${capitalize(outcome)} always`;
  return `${capitalize(outcome)} when ${summarizeCondition(condition)}`;
}

function capitalize(value: string) {
  return value.slice(0, 1).toUpperCase() + value.slice(1);
}

export function summarizeCondition(condition: ConditionIR): string {
  switch (condition.kind) {
    case "always":
      return "always";
    case "all":
      return condition.conditions.map(summarizeCondition).join(" and ");
    case "any":
      return condition.conditions.map(summarizeCondition).join(" or ");
    case "not":
      return `not (${summarizeCondition(condition.condition)})`;
    case "predicate": {
      const path =
        condition.source === "stateMachine"
          ? `stateMachine.${condition.machine || condition.path.join(".")}`
          : `${condition.source}.${condition.path.join(".")}`;
      if (condition.operator === "exists") return `${path} exists`;
      return `${path} ${condition.operator} ${String(condition.value)}`;
    }
  }
}

function makeRule(input: {
  id: string;
  origin: PolicyOrigin;
  outcome: PolicyOutcome;
  reason?: string;
  condition?: ConditionIR;
}): PolicyRuleIR {
  const condition = input.condition || { kind: "always" };
  return {
    ...input,
    condition,
    summary: ruleSummary(input.outcome, input.reason, condition),
  };
}

function actionIdentity(action: string, on?: string) {
  return on ? `${on}:${action}` : `global:${action}`;
}

function normalizeAttachedClass(value?: string | null): string | null {
  if (!value) return null;
  if (!value.startsWith("@")) return value;
  const slashIndex = value.indexOf("/");
  return slashIndex === -1 ? value : value.slice(slashIndex + 1);
}

export function resolveEffectReference(
  effects: EffectDeclarationRef[],
  action: string,
  on?: string,
): EffectDeclarationRef {
  const normalizedOn = normalizeAttachedClass(on);
  const matches = effects.filter((effect) => {
    if (effect.name !== action) return false;
    const attachedClass = normalizeAttachedClass(effect.attachedClass);
    return normalizedOn ? attachedClass === normalizedOn : !attachedClass;
  });
  if (matches.length === 1) return matches[0]!;

  if (!normalizedOn) {
    const byName = effects.filter((effect) => effect.name === action);
    if (byName.length > 1) {
      const classes = byName
        .map(
          (effect) => normalizeAttachedClass(effect.attachedClass) || "global",
        )
        .join(", ");
      throw new Error(
        `Action "${action}" exists on ${classes}. Add "on" to disambiguate.`,
      );
    }
  }

  if (matches.length > 1) {
    throw new Error(`Action "${action}" on "${String(on)}" is ambiguous.`);
  }

  throw new Error(
    on
      ? `Action "${action}" on "${on}" was not found.`
      : `Global action "${action}" was not found.`,
  );
}

function manifestPolicyRulesForEffect(
  effect: EffectDeclarationRef,
  manifestId?: string,
): PolicyRuleIR[] {
  const policies = isRecord(effect.metamodels?.policies)
    ? effect.metamodels?.policies
    : null;
  if (!policies) return [];

  const rules: PolicyRuleIR[] = [];
  const add = (outcome: PolicyOutcome, specs: unknown, prefix: string) => {
    if (!Array.isArray(specs)) return;
    specs.forEach((spec, index) => {
      if (!isRecord(spec)) {
        throw new Error(
          `Manifest policy ${prefix}[${index}] for ${effect.effectKey} must be an object`,
        );
      }
      const condition = normalizeCondition(spec.when);
      rules.push(
        makeRule({
          id:
            typeof spec.id === "string"
              ? spec.id
              : `manifest-${prefix}-${effect.effectKey}-${index + 1}`,
          origin: { kind: "manifest", manifestId, effectKey: effect.effectKey },
          outcome,
          reason: typeof spec.reason === "string" ? spec.reason : undefined,
          condition,
        }),
      );
    });
  };

  add("deny", policies.denyWhen, "denyWhen");
  add("confirm", policies.confirmWhen, "confirmWhen");
  add("allow", policies.allowWhen, "allowWhen");
  return rules;
}

function inputSchemaHasField(effect: EffectDeclarationRef, path: string[]) {
  if (path.length === 0) return false;
  const properties = isRecord(effect.inputSchema?.properties)
    ? (effect.inputSchema?.properties as Record<string, unknown>)
    : {};
  return path[0]! in properties;
}

function validateLimitBands(
  effect: EffectDeclarationRef,
  action: PermissionActionSpec,
  warnings: string[],
) {
  const limits = action.limits || [];
  const byInput = new Map<string, LimitPolicySpec[]>();

  limits.forEach((limit, index) => {
    const path = normalizePath(limit.input);
    if (path.length === 0) {
      throw new Error(`Limit ${index + 1} on ${action.action} requires input`);
    }
    if (!inputSchemaHasField(effect, path)) {
      throw new Error(
        `Limit ${index + 1} on ${action.action} references unsupported input field "${path.join(".")}"`,
      );
    }
    if (!normalizeDecision(limit.decision)) {
      throw new Error(
        `Limit ${index + 1} on ${action.action} has invalid decision`,
      );
    }
    if ((limit.upTo === undefined) === (limit.above === undefined)) {
      throw new Error(
        `Limit ${index + 1} on ${action.action} must define exactly one of upTo or above`,
      );
    }
    if (limit.reason === undefined) {
      warnings.push(`Limit ${index + 1} on ${action.action} has no reason`);
    }
    const key = path.join(".");
    byInput.set(key, [...(byInput.get(key) || []), limit]);
  });

  for (const [input, bands] of byInput.entries()) {
    const thresholds = new Set<string>();
    let previousUpTo = -Infinity;
    for (const band of bands) {
      const value = band.upTo ?? band.above!;
      const key = `${band.upTo === undefined ? "above" : "upTo"}:${value}`;
      if (thresholds.has(key)) {
        throw new Error(
          `Duplicate limit threshold ${key} for input "${input}"`,
        );
      }
      thresholds.add(key);
      if (band.upTo !== undefined) {
        if (band.upTo < previousUpTo) {
          throw new Error(`Unordered limit bands for input "${input}"`);
        }
        previousUpTo = band.upTo;
      }
    }
  }
}

function compileProfileActionRules(input: {
  profile: PermissionProfileFile;
  action: PermissionActionSpec;
  effect: EffectDeclarationRef;
  warnings: string[];
}): PolicyRuleIR[] {
  const { profile, action, effect, warnings } = input;
  const origin: PolicyOrigin = {
    kind: "permissionProfile",
    profileName: action.ownerProfileName || profile.name,
    source: profile.source || "permissionProfile",
  };
  const rules: PolicyRuleIR[] = [];
  const baseId = action.id || `${profile.name}-${effect.effectKey}`;

  if (action.allow === "always") {
    rules.push(
      makeRule({
        id: `${baseId}-allow`,
        origin,
        outcome: "allow",
        reason: action.reason,
      }),
    );
  }
  if (action.confirm === "always") {
    rules.push(
      makeRule({
        id: `${baseId}-confirm`,
        origin,
        outcome: "confirm",
        reason: action.reason,
      }),
    );
  }
  if (action.deny === "always") {
    rules.push(
      makeRule({
        id: `${baseId}-deny`,
        origin,
        outcome: "deny",
        reason: action.reason,
      }),
    );
  }

  const addConditional = (
    outcome: PolicyOutcome,
    specs: ConditionalPolicySpec[] | undefined,
    suffix: string,
  ) => {
    (specs || []).forEach((spec, index) => {
      rules.push(
        makeRule({
          id: spec.id || `${baseId}-${suffix}-${index + 1}`,
          origin,
          outcome,
          reason: spec.reason,
          condition: normalizeCondition(spec.when),
        }),
      );
    });
  };

  addConditional("allow", action.allowWhen, "allowWhen");
  addConditional("confirm", action.confirmWhen, "confirmWhen");
  addConditional("deny", action.denyWhen, "denyWhen");

  validateLimitBands(effect, action, warnings);
  const previousUpToByInput = new Map<string, number>();
  (action.limits || []).forEach((limit, index) => {
    const path = normalizePath(limit.input);
    const pathKey = path.join(".");
    const lowerBound = previousUpToByInput.get(pathKey);
    const upperCondition: ConditionIR = {
      kind: "predicate",
      source: "input",
      path,
      operator: limit.upTo !== undefined ? "lte" : "gt",
      value: limit.upTo ?? limit.above,
    };
    const condition =
      lowerBound !== undefined && limit.upTo !== undefined
        ? {
            kind: "all" as const,
            conditions: [
              {
                kind: "predicate" as const,
                source: "input" as const,
                path,
                operator: "gt" as const,
                value: lowerBound,
              },
              upperCondition,
            ],
          }
        : upperCondition;

    rules.push(
      makeRule({
        id: limit.id || `${baseId}-limit-${index + 1}`,
        origin,
        outcome: limit.decision,
        reason: limit.reason,
        condition,
      }),
    );
    if (limit.upTo !== undefined) previousUpToByInput.set(pathKey, limit.upTo);
  });

  return rules;
}

function flattenProfiles(
  profiles: PermissionProfileFile[],
  errors: string[],
): PermissionProfileFile[] {
  const byName = new Map<string, PermissionProfileFile>();
  for (const profile of profiles) {
    const name = normalizeProfileName(profile.name);
    if (!name) {
      errors.push(`Invalid permission profile name: ${String(profile.name)}`);
      continue;
    }
    if (byName.has(name)) {
      errors.push(`Duplicate permission profile name: ${name}`);
      continue;
    }
    byName.set(name, {
      ...profile,
      name,
      defaultsOwnerName: profile.defaultsOwnerName || name,
      policies: (profile.policies || []).map((policy) => ({
        ...policy,
        ownerProfileName: policy.ownerProfileName || name,
      })),
      actions: (profile.actions || []).map((action) => ({
        ...action,
        ownerProfileName: action.ownerProfileName || name,
      })),
    });
  }

  const flattening = new Set<string>();
  const flattened = new Map<string, PermissionProfileFile>();

  const flatten = (
    name: string,
    depth: number,
  ): PermissionProfileFile | null => {
    if (depth > 5) {
      errors.push(`Permission profile inheritance depth exceeds 5 at ${name}`);
      return null;
    }
    const existing = flattened.get(name);
    if (existing) return existing;
    const profile = byName.get(name);
    if (!profile) {
      errors.push(`Missing parent permission profile: ${name}`);
      return null;
    }
    if (flattening.has(name)) {
      errors.push(`Permission profile inheritance cycle detected at ${name}`);
      return null;
    }
    flattening.add(name);

    let result = profile;
    if (profile.extends) {
      if (profile.defaults !== undefined) {
        errors.push(
          `Permission profile "${name}" extends "${profile.extends}" and cannot define defaults`,
        );
      }
      const parent = flatten(profile.extends, depth + 1);
      if (parent) {
        const actionMap = new Map<string, PermissionActionSpec>();
        for (const action of parent.actions || []) {
          actionMap.set(actionIdentity(action.action, action.on), action);
        }
        for (const action of profile.actions || []) {
          actionMap.set(actionIdentity(action.action, action.on), action);
        }
        result = {
          ...profile,
          defaults: parent.defaults,
          defaultsOwnerName: parent.defaultsOwnerName || parent.name,
          policies: [...(parent.policies || []), ...(profile.policies || [])],
          actions: [...actionMap.values()],
        };
      }
    }

    flattening.delete(name);
    flattened.set(name, result);
    return result;
  };

  for (const name of byName.keys()) flatten(name, 0);
  return [...flattened.values()];
}

function compileProfile(input: {
  profile: PermissionProfileFile;
  effects: EffectDeclarationRef[];
  manifestRules: Record<string, PolicyRuleIR[]>;
  warnings: string[];
}): CompiledPermissionProfile {
  const { profile, effects, manifestRules, warnings } = input;
  const defaultActionPolicy =
    normalizeDecision(profile.defaults?.actionPolicy) || "deny";
  const actionRules: Record<string, PolicyRuleIR[]> = {};

  const addRules = (effectKey: string, rules: PolicyRuleIR[]) => {
    if (rules.length === 0) return;
    actionRules[effectKey] = [...(actionRules[effectKey] || []), ...rules];
  };

  for (const policy of profile.policies || []) {
    const outcome = normalizeDecision(policy.outcome || policy.decision);
    if (!outcome)
      throw new Error(
        `Profile "${profile.name}" has a policy with invalid decision`,
      );
    const condition = normalizeCondition(policy.when);
    const targetEffects = policy.action
      ? [resolveEffectReference(effects, policy.action, policy.on)]
      : effects;
    for (const effect of targetEffects) {
      addRules(effect.effectKey, [
        makeRule({
          id: policy.id || `${profile.name}-${effect.effectKey}-profile-policy`,
          origin: {
            kind: "permissionProfile",
            profileName: policy.ownerProfileName || profile.name,
            source: profile.source || "permissionProfile",
          },
          outcome,
          reason: policy.reason,
          condition,
        }),
      ]);
    }
  }

  for (const action of profile.actions || []) {
    const effect = resolveEffectReference(effects, action.action, action.on);
    addRules(
      effect.effectKey,
      compileProfileActionRules({ profile, action, effect, warnings }),
    );
  }

  const actionVisibility = effects
    .filter((effect) => {
      const rules = actionRules[effect.effectKey] || [];
      const denyVisibilityRules = [
        ...(manifestRules[effect.effectKey] || []),
        ...rules,
      ];
      if (
        denyVisibilityRules.some(
          (rule) => rule.outcome === "deny" && rule.condition.kind === "always",
        )
      ) {
        return false;
      }
      if (defaultActionPolicy === "allow" || defaultActionPolicy === "confirm")
        return true;
      return denyVisibilityRules.some(
        (rule) => rule.outcome === "allow" || rule.outcome === "confirm",
      );
    })
    .map((effect) => effect.effectKey)
    .sort();

  const summaries = Object.entries(actionRules)
    .flatMap(([effectKey, rules]) =>
      rules.map((rule) => `${effectKey}: ${rule.summary}`),
    )
    .sort();

  return {
    name: profile.name,
    label: profile.label,
    description: profile.description,
    source: profile.source || "permissionProfile",
    sourcePath: profile.sourcePath,
    defaultActionPolicy,
    defaultActionPolicyOwner: profile.defaultsOwnerName || profile.name,
    digest: "",
    actionRules,
    actionVisibility,
    summaries,
    flattened: profile,
  };
}

function normalizeSourceProfiles(sources: PermissionProfileFile[] | undefined) {
  const sourceProfiles = (sources || [])
    .filter((profile) => isRecord(profile))
    .map((profile) => ({
      ...profile,
      source: profile.source || "permissionProfile",
    }));

  const nonBuiltin = sourceProfiles.filter(
    (profile) =>
      !BUILTIN_PROFILE_NAMES.includes(
        profile.name as (typeof BUILTIN_PROFILE_NAMES)[number],
      ),
  );
  return [...getBuiltinPermissionProfiles(), ...nonBuiltin];
}

export async function compilePolicyBundle(
  input: CompilePolicyBundleInput,
): Promise<CompilePolicyBundleResult> {
  const errors: string[] = [];
  const warnings: string[] = [];
  const effects = [...input.effects].sort((left, right) =>
    left.effectKey.localeCompare(right.effectKey),
  );
  const profiles = flattenProfiles(
    normalizeSourceProfiles(input.profileSources),
    errors,
  );

  const manifestRules: Record<string, PolicyRuleIR[]> = {};
  for (const effect of effects) {
    try {
      const rules = manifestPolicyRulesForEffect(effect, input.manifestId);
      if (rules.length > 0) manifestRules[effect.effectKey] = rules;
    } catch (error) {
      errors.push(String(error instanceof Error ? error.message : error));
    }
  }

  const compiledProfiles: Record<string, CompiledPermissionProfile> = {};
  for (const profile of profiles) {
    try {
      const defaultActionPolicy = profile.defaults?.actionPolicy;
      if (
        defaultActionPolicy !== undefined &&
        !normalizeDecision(defaultActionPolicy)
      ) {
        throw new Error(
          `Permission profile "${profile.name}" has invalid defaults.actionPolicy`,
        );
      }
      const compiled = compileProfile({
        profile,
        effects,
        manifestRules,
        warnings,
      });
      compiled.digest = await digestJson({
        profile: compiled.flattened,
        actionRules: compiled.actionRules,
        defaultActionPolicy: compiled.defaultActionPolicy,
      });
      compiledProfiles[profile.name] = compiled;
    } catch (error) {
      errors.push(String(error instanceof Error ? error.message : error));
    }
  }

  const permissionProfilesDigest = await digestJson(
    Object.values(compiledProfiles).map((profile) => ({
      name: profile.name,
      digest: profile.digest,
      source: profile.source,
    })),
  );

  const bundleWithoutDigest = {
    schemaVersion: 1 as const,
    buildId: input.buildId,
    sandboxId: input.sandboxId,
    manifestDigest: input.manifestDigest,
    permissionProfilesDigest,
    generatedAt: new Date().toISOString(),
    effects: effects.map((effect) => ({
      effectKey: effect.effectKey,
      name: effect.name,
      attachedClass: normalizeAttachedClass(effect.attachedClass),
      isStatic: effect.isStatic === true,
    })),
    manifestRules,
    profiles: compiledProfiles,
  };

  const bundleDigest = await digestJson(bundleWithoutDigest);
  return {
    bundle: { ...bundleWithoutDigest, bundleDigest },
    errors,
    warnings,
  };
}

export function evaluateCompiledPolicyBundle(input: {
  bundle: CompiledPolicyBundle;
  profileName: string;
  effectKey: string;
  context?: PolicyEvaluationContext;
}): PolicyDecision {
  const profile = input.bundle.profiles[input.profileName];
  if (!profile) {
    return {
      outcome: "deny",
      matches: [
        {
          id: "missing-profile",
          outcome: "deny",
          origin: {
            kind: "permissionProfile",
            profileName: input.profileName,
            source: "permissionProfile",
          },
          reason: `Permission profile "${input.profileName}" does not exist in this version`,
          summary: `Permission profile "${input.profileName}" does not exist in this version`,
        },
      ],
    };
  }

  if (!profile.actionVisibility.includes(input.effectKey)) {
    return {
      outcome: "deny",
      matches: [
        {
          id: "action-not-visible",
          outcome: "deny",
          origin: {
            kind: "permissionProfile",
            profileName: input.profileName,
            source: profile.source,
          },
          reason: `Action ${input.effectKey} is not visible for ${input.profileName}`,
          summary: `Action ${input.effectKey} is not visible for ${input.profileName}`,
        },
      ],
    };
  }

  const decision = evaluatePolicies({
    manifestRules: input.bundle.manifestRules[input.effectKey] || [],
    profileRules: profile.actionRules[input.effectKey] || [],
    defaultActionPolicy: profile.defaultActionPolicy,
    context: input.context,
  });

  return {
    outcome: decision.outcome,
    matches: decision.matches.map((match) =>
      match.id === `profile-default-${decision.outcome}`
        ? {
            ...match,
            origin: {
              kind: "permissionProfile",
              profileName: profile.name,
              source: profile.source,
            },
          }
        : match,
    ),
  };
}

export function extractManifestEffects(manifest: {
  volumes?: Array<{ operations?: Array<{ withEffect?: any }> }>;
}): EffectDeclarationRef[] {
  const effects: EffectDeclarationRef[] = [];
  for (const volume of manifest.volumes || []) {
    for (const operation of volume.operations || []) {
      const effect = operation.withEffect;
      if (!effect?.name) continue;
      const attachedClass = normalizeAttachedClass(effect.attachedClass);
      const effectKey = attachedClass
        ? effect.isStatic
          ? `class:${attachedClass}:static:${effect.name}`
          : `class:${attachedClass}:instance:${effect.name}`
        : `global:${effect.name}`;
      effects.push({
        effectKey,
        name: effect.name,
        attachedClass,
        isStatic: effect.isStatic === true,
        inputSchema: effect.inputSchema || null,
        metamodels: effect.metamodels || null,
      });
    }
  }
  return effects;
}

export function listVisibleActionsForProfile(
  bundle: CompiledPolicyBundle,
  profileName: string,
): string[] {
  return [...(bundle.profiles[profileName]?.actionVisibility || [])];
}
